import React from 'react';
import { Theme } from '@mui/material/styles';

import createStyles from '@mui/styles/createStyles';
import makeStyles from '@mui/styles/makeStyles';
import { clsx } from 'clsx';

export interface ModalButtonContainerProps {
    children: React.ReactNode;
    displayEnd?: boolean;
    noMargin?: boolean;
    /**
     * Stack the buttons vertically, each spanning the modal's width, and let a
     * label wrap onto a second line instead of being clipped. For modals whose
     * primary action is a sentence ("Changer de moyen de paiement") rather
     * than a verb: side by side, ModalButton's 160px / 49px caps cut it off
     * (LT-1884, operator 18/09). Opt-in — the row layout stays the default.
     */
    column?: boolean;
}

const ModalButtonContainer = ({
    children,
    displayEnd = false,
    noMargin = false,
    column = false,
}: ModalButtonContainerProps): JSX.Element => {
    const classes = useStyles();
    if (column) {
        return <div className={clsx(classes.columnContainer, noMargin && classes.noMargin)}>{children}</div>;
    }
    return (
        <div
            className={
                displayEnd
                    ? clsx('LCEndContainer', classes.endContainer, noMargin && classes.noMargin)
                    : classes.container
            }
        >
            {children}
        </div>
    );
};

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        container: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginTop: theme.spacing(4),
            backgroundColor: theme.palette.colors.background.appBar,
        },
        endContainer: {
            display: 'flex',
            alignItems: 'end',
            justifyContent: 'end',
            marginTop: theme.spacing(4),
            backgroundColor: theme.palette.colors.background.appBar,
        },
        noMargin: {
            marginTop: 0,
        },
        columnContainer: {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'stretch',
            gap: theme.spacing(1.5),
            marginTop: theme.spacing(4),
            backgroundColor: theme.palette.colors.background.appBar,
            // Lift every cap ModalButton sets for the row layout: the button
            // takes the full width, and grows in height if its label wraps.
            // Nothing is ever clipped, whatever the label or the screen.
            '& > .MuiButton-root': {
                width: '100%',
                minWidth: 'unset',
                maxWidth: 'unset',
                maxHeight: 'unset',
                marginLeft: 0,
                marginRight: 0,
                marginBottom: 0,
                whiteSpace: 'normal',
                lineHeight: 1.3,
            },
        },
    }),
);

export default ModalButtonContainer;
